import { api } from '@/lib/axios'
import { mitStand, type Datensatz } from '@/lib/speichern'
import type { DashboardAntwort } from '@symphenia/shared-types'
import type { ExportRequest } from '@/lib/export-request'
import type {
  OverheadParams,
  Reading,
  ReadingPairing,
  ReadingSelection,
  ReadingState,
  OverheadReport,
  ApprovalRun,
  BankImportResult,
  ExportBatch,
  ExportProfile,
  ExportSelectionRow,
  DeductionCandidate,
  HfuCheck,
  OpenRetention,
  RetentionOverview,
  IncomingPayment,
  OpenItemsSummary,
  PaymentRun,
  RunProposal,
  IncomingFrame,
  IncomingSettings,
  IncomingInvoiceDetail,
  IncomingInvoiceRow,
  LedgerImportResult,
  ListIncomingParams,
  LookupPosition,
  LookupArticle,
  LookupProject,
  LookupSupplier,
  OpenPosition,
  Paginated,
  PairingChoice,
} from '../types/incoming-invoices.types'

const B = '/incoming-invoices'
type Antwort = { data: IncomingInvoiceDetail }

export const fetchIncomingInvoices = (params: ListIncomingParams) => api.get<Paginated<IncomingInvoiceRow>>(B, { params }).then((r) => r.data)
export const fetchIncomingInvoice = (id: string) => api.get<Antwort>(`${B}/${id}`).then((r) => r.data.data)
export const fetchIncomingFrame = () => api.get<{ data: IncomingFrame }>(`${B}/settings/public`).then((r) => r.data.data)

export const createIncomingInvoice = (supplierId: string, kind: string) => api.post<Antwort>(B, { supplierId, kind }).then((r) => r.data.data)
export const deleteIncomingInvoice = (id: string) => api.delete(`${B}/${id}`)

/** Kopf feldweise — die Antwort ist die ganze Rechnung in GET-Form. */
export const patchIncomingInvoice = (id: string, patch: Datensatz, stand?: string) => api.patch<Antwort>(`${B}/${id}`, patch, mitStand(stand)).then((r) => r.data.data)

export const createIncomingLine = (id: string, body: Datensatz) => api.post<Antwort>(`${B}/${id}/lines`, body).then((r) => r.data.data)
export const patchIncomingLine = (id: string, lineId: string, patch: Datensatz, stand?: string) =>
  api.patch<Antwort>(`${B}/${id}/lines/${lineId}`, patch, mitStand(stand)).then((r) => r.data.data)
export const deleteIncomingLine = (id: string, lineId: string) => api.delete<Antwort>(`${B}/${id}/lines/${lineId}`).then((r) => r.data.data)
/** Die neue Reihenfolge — alle Zeilen der Rechnung. */
export const reorderIncomingLines = (id: string, ids: string[]) => api.post<Antwort>(`${B}/${id}/lines/reorder`, { ids }).then((r) => r.data.data)
/** Kopierte Zeilen einfügen — vor `beforeId`, sonst am Ende. */
export const pasteIncomingLines = (id: string, lineIds: string[], beforeId?: string | null) => api.post<Antwort>(`${B}/${id}/lines/paste`, { lineIds, ...(beforeId && { beforeId }) }).then((r) => r.data.data)

export async function uploadIncomingInvoices(dateien: File[]): Promise<IncomingInvoiceRow[]> {
  const form = new FormData()
  for (const d of dateien) form.append('files', d)
  const r = await api.post<{ data: IncomingInvoiceRow[] }>(`${B}/upload`, form)
  return r.data.data
}

/** Das Original als Bytes — für die Anzeige mit pdf.js bzw. als Bild. */
export async function fetchIncomingOriginal(id: string): Promise<{ daten: ArrayBuffer; mimeType: string }> {
  const r = await api.get<ArrayBuffer>(`${B}/${id}/file`, { responseType: 'arraybuffer' })
  return { daten: r.data, mimeType: String(r.headers['content-type'] ?? '').split(';')[0] }
}

export const lookupSuppliers = (search: string) => api.get<{ data: LookupSupplier[] }>(`${B}/lookup/suppliers`, { params: { search } }).then((r) => r.data.data)
export const lookupArticles = (search: string) => api.get<{ data: LookupArticle[] }>(`${B}/lookup/articles`, { params: { search } }).then((r) => r.data.data)
export const lookupProjects = (search: string) => api.get<{ data: LookupProject[] }>(`${B}/lookup/projects`, { params: { search } }).then((r) => r.data.data)
export const lookupPositions = (projectId: string) => api.get<{ data: LookupPosition[] }>(`${B}/lookup/projects/${projectId}/positions`).then((r) => r.data.data)

// ── Abgleich (9.) ──
export const fetchOpenPositions = (id: string) => api.get<{ data: OpenPosition[] }>(`${B}/${id}/match/open`).then((r) => r.data.data)
export const takeOpenPositions = (id: string, sourceLineIds: string[]) => api.post<Antwort>(`${B}/${id}/match/take`, { sourceLineIds }).then((r) => r.data.data)
export const suggestPairings = (id: string) => api.post<{ data: IncomingInvoiceDetail; choices: PairingChoice[] }>(`${B}/${id}/match/suggest`).then((r) => r.data)
export const confirmPairings = (id: string, lineIds?: string[]) => api.post<Antwort>(`${B}/${id}/match/confirm`, lineIds ? { lineIds } : {}).then((r) => r.data.data)
/** Bezug einer Zeile setzen; `null` löst ihn — mit `withoutOrder` bleibt sie ausdrücklich „ohne Bezug" (Nachtrag 3, E-8). */
export const setLineSource = (id: string, lineId: string, sourceLineId: string | null, stand?: string, withoutOrder?: boolean) =>
  api.put<Antwort>(`${B}/${id}/lines/${lineId}/source`, { sourceLineId, ...(withoutOrder !== undefined && { withoutOrder }) }, mitStand(stand)).then((r) => r.data.data)

// ── Abschließen und Freigabe (11.) ──
export const completeIncomingInvoice = (id: string) => api.post<Antwort>(`${B}/${id}/complete`).then((r) => r.data.data)
export const fetchApprovalRun = (id: string) => api.get<{ data: ApprovalRun }>(`${B}/${id}/approval`).then((r) => r.data.data)
export const cancelApprovalRun = (id: string, reason: string) => api.post<Antwort>(`${B}/${id}/approval/cancel`, { reason }).then((r) => r.data.data)
export const decideApproval = (taskId: string, decision: 'APPROVE' | 'REJECT', comment?: string | null) =>
  api.post<Antwort>(`${B}/approvals/${taskId}/decide`, { decision, comment: comment || null }).then((r) => r.data.data)
export const reassignApproval = (taskId: string, userId: string) => api.post<{ data: ApprovalRun }>(`${B}/approvals/${taskId}/reassign`, { userId }).then((r) => r.data.data)
export const fetchApprovers = () => api.get<{ data: { id: string; name: string }[] }>(`${B}/approvals/approvers`).then((r) => r.data.data)

// ── Einstellungen (18.) ──
type Einst = { data: IncomingSettings }
export const fetchIncomingSettings = () => api.get<Einst>(`${B}/settings`).then((r) => r.data.data)
export const patchIncomingSettings = (patch: Datensatz, stand?: string) => api.patch<Einst>(`${B}/settings`, patch, mitStand(stand)).then((r) => r.data.data)
export const createApprovalRule = (body: Datensatz) => api.post<Einst>(`${B}/settings/rules`, body).then((r) => r.data.data)
export const patchApprovalRule = (id: string, patch: Datensatz, stand?: string) => api.patch<Einst>(`${B}/settings/rules/${id}`, patch, mitStand(stand)).then((r) => r.data.data)
export const deleteApprovalRule = (id: string) => api.delete<Einst>(`${B}/settings/rules/${id}`).then((r) => r.data.data)
export const createOverheadCategory = (name: string) => api.post<Einst>(`${B}/settings/categories`, { name }).then((r) => r.data.data)
export const patchOverheadCategory = (id: string, patch: Datensatz, stand?: string) => api.patch<Einst>(`${B}/settings/categories/${id}`, patch, mitStand(stand)).then((r) => r.data.data)
export const createLedgerAccount = (number: string, name: string) => api.post<Einst>(`${B}/settings/accounts`, { number, name }).then((r) => r.data.data)
export const patchLedgerAccount = (id: string, patch: Datensatz, stand?: string) => api.patch<Einst>(`${B}/settings/accounts/${id}`, patch, mitStand(stand)).then((r) => r.data.data)
/**
 * Sachkonten einlesen — eine Datei (CSV, XLSX) oder Kopiertes. Mit `dryRun` kommt nur die Vorschau; dieselbe
 * Anfrage ohne `dryRun` legt an. Ohne Angabe erkennt der Server Überschrift und Spalten selbst.
 */
export async function importLedgerAccounts(
  quelle: { datei?: File | null; text?: string },
  optionen: { dryRun?: boolean; hasHeader?: boolean; numberColumn?: number; nameColumn?: number; updateNames?: boolean },
): Promise<LedgerImportResult> {
  const form = new FormData()
  if (quelle.datei) form.append('file', quelle.datei)
  else if (quelle.text) form.append('text', quelle.text)
  for (const [feld, wert] of Object.entries(optionen)) if (wert !== undefined) form.append(feld, String(wert))
  const r = await api.post<{ data: LedgerImportResult }>(`${B}/settings/accounts/import`, form)
  return r.data.data
}

// ── Zahlung (12.) ──
export const fetchOpenItemsSummary = () => api.get<{ data: OpenItemsSummary }>(`${B}/open-items/summary`).then((r) => r.data.data)
export const fetchPayments = (id: string) => api.get<{ data: IncomingPayment[] }>(`${B}/${id}/payments`).then((r) => r.data.data)
export const addPayment = (id: string, body: { paidOn: string; amount?: number; discountAmount?: number; note?: string | null }) => api.post<Antwort>(`${B}/${id}/payments`, body).then((r) => r.data.data)
export const deletePayment = (id: string, paymentId: string) => api.delete<Antwort>(`${B}/${id}/payments/${paymentId}`).then((r) => r.data.data)
export const blockPayment = (id: string, reason: string) => api.post<Antwort>(`${B}/${id}/payment-block`, { reason }).then((r) => r.data.data)
export const releasePaymentBlock = (id: string, body: { mode?: 'ADOPT' | 'THIS_ONLY'; comment?: string | null }) => api.post<Antwort>(`${B}/${id}/payment-block/release`, body).then((r) => r.data.data)

type LaufAntwort = { data: PaymentRun }
const L = `${B}/payment-runs`
export const fetchRunProposal = (dueUntil: string, executionDate: string) => api.post<{ data: RunProposal }>(`${L}/proposal`, { dueUntil, executionDate }).then((r) => r.data.data)
export const createPaymentRun = (executionDate: string, items: { invoiceId: string; amount: number; discountAmount: number }[]) => api.post<LaufAntwort>(L, { executionDate, items }).then((r) => r.data.data)
export const fetchPaymentRuns = () => api.get<{ data: PaymentRun[] }>(L).then((r) => r.data.data)
export const fetchPaymentRun = (id: string) => api.get<LaufAntwort>(`${L}/${id}`).then((r) => r.data.data)
export const patchRunItem = (id: string, itemId: string, amount: number, discountAmount: number) => api.patch<LaufAntwort>(`${L}/${id}/items/${itemId}`, { amount, discountAmount }).then((r) => r.data.data)
export const removeRunItem = (id: string, itemId: string) => api.delete<LaufAntwort>(`${L}/${id}/items/${itemId}`).then((r) => r.data.data)
/** `release` · `file` · `complete` · `cancel` */
export const runAction = (id: string, aktion: 'release' | 'file' | 'complete' | 'cancel') => api.post<LaufAntwort>(`${L}/${id}/${aktion}`).then((r) => r.data.data)
export async function downloadRunFile(id: string, nummer: string): Promise<void> {
  const r = await api.get<Blob>(`${L}/${id}/file`, { responseType: 'blob' })
  const url = URL.createObjectURL(r.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `Zahlungslauf-${nummer}.xml`
  a.click()
  URL.revokeObjectURL(url)
}

export async function uploadBankStatement(datei: File, csv?: Record<string, string>): Promise<BankImportResult> {
  const form = new FormData()
  form.append('file', datei)
  for (const [k, v] of Object.entries(csv ?? {})) form.append(k, v)
  const r = await api.post<{ data: BankImportResult }>(`${B}/bank-imports`, form)
  return r.data.data
}
export const assignBankEntry = (importId: string, index: number, invoiceId: string | null) => api.post<{ data: BankImportResult }>(`${B}/bank-imports/${importId}/assign`, { index, invoiceId }).then((r) => r.data.data)

// ── Subunternehmer (15.), Schlussrechnung (8.3) ──
export const fetchHfuChecks = (supplierId: string) => api.get<{ data: HfuCheck[] }>(`${B}/hfu-checks`, { params: { supplierId } }).then((r) => r.data.data)
export async function addHfuCheck(supplierId: string, checkedOn: string, listed: boolean, beleg?: File | null): Promise<HfuCheck[]> {
  const form = new FormData()
  form.append('supplierId', supplierId)
  form.append('checkedOn', checkedOn)
  form.append('listed', String(listed))
  if (beleg) form.append('file', beleg)
  const r = await api.post<{ data: HfuCheck[] }>(`${B}/hfu-checks`, form)
  return r.data.data
}
type Rueck = { data: RetentionOverview }
export const fetchRetentions = (id: string) => api.get<Rueck>(`${B}/${id}/retentions`).then((r) => r.data.data)
export const addRetention = (id: string, body: { kind: string; percent: number }) => api.post<Rueck>(`${B}/${id}/retentions`, body).then((r) => r.data.data)
export const patchRetention = (id: string, rid: string, body: Datensatz) => api.patch<Rueck>(`${B}/${id}/retentions/${rid}`, body).then((r) => r.data.data)
export const deleteRetention = (id: string, rid: string) => api.delete<Rueck>(`${B}/${id}/retentions/${rid}`).then((r) => r.data.data)
export const releaseRetention = (id: string, rid: string) => api.post<Rueck>(`${B}/${id}/retentions/${rid}/release`).then((r) => r.data.data)
export const releaseCoverRetentions = (id: string) => api.post<Rueck>(`${B}/${id}/retentions/release-cover`).then((r) => r.data.data)
export const fetchOpenRetentions = () => api.get<{ data: OpenRetention[] }>(`${B}/open-items/retentions`).then((r) => r.data.data)
export const fetchDeductionCandidates = (id: string) => api.get<{ data: DeductionCandidate[] }>(`${B}/${id}/deductions/candidates`).then((r) => r.data.data)
export const deductInvoices = (id: string, invoiceIds: string[]) => api.post<Antwort>(`${B}/${id}/deductions`, { invoiceIds }).then((r) => r.data.data)

// ── Übergabe (13.), Listenexport, Korrektur (5.2) ──
const H = `${B}/handover`
type Profile = { profiles: ExportProfile[]; taxCodes: { key: string; label: string }[] }
export const fetchExportProfiles = () => api.get<{ data: Profile }>(`${H}/profiles`).then((r) => r.data.data)
export const patchExportProfile = (id: string, patch: Datensatz) => api.patch<{ data: Profile }>(`${H}/profiles/${id}`, patch).then((r) => r.data.data)
export const fetchExportSelection = (until: string, profileId: string) => api.get<{ data: ExportSelectionRow[] }>(`${H}/selection`, { params: { until, profileId } }).then((r) => r.data.data)
export const fetchExportBatches = () => api.get<{ data: ExportBatch[] }>(`${H}/batches`).then((r) => r.data.data)
export const createExportBatch = (profileId: string, invoiceIds: string[]) => api.post<{ data: ExportBatch[] }>(`${H}/batches`, { profileId, invoiceIds }).then((r) => r.data.data)
export const revokeExportBatch = (id: string, reason: string) => api.post<{ data: ExportBatch[] }>(`${H}/batches/${id}/revoke`, { reason }).then((r) => r.data.data)
/** Die Datei des Stapels — als Anfrage: Download und Mailversand nehmen dieselbe. */
export const batchFileRequest = (id: string): ExportRequest => ({ url: `${H}/batches/${id}/export` })
/** Rechnungsliste bzw. offene Posten als Datei — mit den Filtern der Liste. */
export const incomingExportRequest = (params: ListIncomingParams & { format: string; orientation?: string; filterInfo?: string }, columns: { key: string; label: string }[]): ExportRequest => ({
  url: `${B}/export`,
  params: { ...params, columns: JSON.stringify(columns) },
})
// ── Phase N: Dashboard ──
export const fetchIncomingDashboard = (scope: 'all' | 'mine') => api.get<{ data: DashboardAntwort }>(`${B}/dashboard`, { params: { scope } }).then((r) => r.data.data)

// ── Phase K: KI-Lesung ──
export const fetchReadingState = () => api.get<{ data: ReadingState }>(`${B}/reading`).then((r) => r.data.data)
export const setReadingConsent = (consent: boolean) => api.post<{ data: ReadingState }>(`${B}/reading/consent`, { consent }).then((r) => r.data.data)
/** Geschrieben wird nur das gesendete Feld: nach dem Hochladen lesen, nach dem Übernehmen Sachkonten vorschlagen. */
export const setReadingAuto = (patch: { autoRead?: boolean; autoAccount?: boolean }) => api.patch<{ data: ReadingState }>(`${B}/reading`, patch).then((r) => r.data.data)
export const fetchReading = (id: string) => api.get<{ data: Reading }>(`${B}/${id}/reading`).then((r) => r.data.data)
/** Nachtrag 4, PT-18 — „Stapel mit KI lesen": startet die Lesung für alles im Eingangsstapel, das noch nicht gelesen ist. */
export const startStackReading = () => api.post<{ data: { started: number; skipped: number; more: boolean } }>(`${B}/reading/stack`).then((r) => r.data.data)
export const startReading = (id: string) => api.post<{ data: Reading }>(`${B}/${id}/reading`).then((r) => r.data.data)
export const applyReading = (id: string, auswahl: ReadingSelection) => api.post<{ data: { invoice: IncomingInvoiceDetail; reading: Reading; pairing: ReadingPairing | null } }>(`${B}/${id}/reading/apply`, auswahl).then((r) => r.data.data)
/** Den gelesenen Aussteller als Lieferanten anlegen und an die Rechnung setzen; `omitted`: was dabei wegblieb. */
export const createReadingSupplier = (id: string, allowDuplicate = false) =>
  api.post<{ data: { invoice: IncomingInvoiceDetail; reading: Reading; supplier: { id: string; name: string; omitted: string[] } } }>(`${B}/${id}/reading/supplier`, { allowDuplicate }).then((r) => r.data.data)
export const discardReading = (id: string, auswahl: ReadingSelection) => api.post<{ data: { invoice: IncomingInvoiceDetail; reading: Reading } }>(`${B}/${id}/reading/discard`, auswahl).then((r) => r.data.data)

// ── AP-44 7.: Sachkonto-Vorschlag der KI — die Antwort ist jedes Mal die ganze Rechnung ──
/** `invoice`: der Vorschlag an der Rechnung · `lineIds`: die an diesen Positionen. */
export type AccountSuggestionSelection = { invoice?: boolean; lineIds?: string[] }
export const suggestAccounts = (id: string) => api.post<Antwort>(`${B}/${id}/account-suggestions`).then((r) => r.data.data)
/** Ohne Auswahl: alle sicheren. */
export const applyAccountSuggestions = (id: string, auswahl: AccountSuggestionSelection = {}) => api.post<Antwort>(`${B}/${id}/account-suggestions/apply`, auswahl).then((r) => r.data.data)
/** Ohne Auswahl: alle. */
export const discardAccountSuggestions = (id: string, auswahl: AccountSuggestionSelection = {}) => api.post<Antwort>(`${B}/${id}/account-suggestions/discard`, auswahl).then((r) => r.data.data)

// ── Phase J: Kosten ──
export const fetchOverhead = (params: OverheadParams) => api.get<{ data: OverheadReport }>(`${B}/overhead`, { params }).then((r) => r.data.data)
export const overheadExportRequest = (params: OverheadParams & { format: string; orientation?: string; filterInfo?: string }, columns: { key: string; label: string }[]): ExportRequest => ({
  url: `${B}/overhead/export`,
  params: { ...params, columns: JSON.stringify(columns) },
})
export const cancelIncomingInvoice = (id: string, reason: string) => api.post<Antwort>(`${B}/${id}/cancel`, { reason }).then((r) => r.data.data)
export const reopenIncomingInvoice = (id: string) => api.post<Antwort>(`${B}/${id}/reopen`).then((r) => r.data.data)
export const reverseIncomingInvoice = (id: string) => api.post<Antwort>(`${B}/${id}/reversal`).then((r) => r.data.data)
